/**
 * Effective (chained) beneficial ownership over the declared ownership graph.
 *
 * Owners are a flat list linked by `parentOwnerId`. A top-level owner holds its
 * `ownershipPct` directly of the expediente's subject (the ROOT persona jurídica);
 * a nested owner holds its `ownershipPct` of its PARENT, not of the root. So the
 * effective stake of any owner in the root is the PRODUCT of the edge weights down
 * its chain:
 *
 *   Rosa posee 100% de Fundación Mar Adentro, que posee 24% del sujeto
 *     → participación efectiva de Rosa en el sujeto = 100% × 24% = 24%.
 *
 * This mirrors the API's UBO workflow (packages/api/src/validations/workflows/ubo),
 * which multiplies the same way; the frontend now agrees with it instead of reading
 * each owner's raw edge weight. Percentages are on the 0–100 scale in and out.
 *
 * Cycles (A→B→A) are broken by treating a node whose ancestor chain loops back on
 * itself as a root — the same resilience OwnersPanel/OwnershipGraph already apply so
 * a bad `parentOwnerId` never hides a node. Missing/NaN `ownershipPct` counts as 0.
 */

/** The structural subset of `Owner` this computation needs (keeps it test-friendly). */
export interface OwnershipNode {
  id: string;
  parentOwnerId: string | null;
  ownershipPct: number;
}

/**
 * Effective participation (%) in the root subject, keyed by owner id.
 * Value is on the 0–100 scale, i.e. `24` means 24% of the root persona jurídica.
 */
export function effectiveOwnership(owners: readonly OwnershipNode[]): Map<string, number> {
  const byId = new Map(owners.map((o) => [o.id, o]));

  // An owner whose ancestor chain loops (A→B→A) has no well-defined root, so we
  // treat it as a root (parent = undefined) — matching the tree builders in the UI.
  const inCycle = (o: OwnershipNode): boolean => {
    const seen = new Set<string>([o.id]);
    let cur = o.parentOwnerId ? byId.get(o.parentOwnerId) : undefined;
    while (cur) {
      if (seen.has(cur.id)) return true;
      seen.add(cur.id);
      cur = cur.parentOwnerId ? byId.get(cur.parentOwnerId) : undefined;
    }
    return false;
  };

  const parentOf = (o: OwnershipNode): OwnershipNode | undefined => {
    if (!o.parentOwnerId || !byId.has(o.parentOwnerId) || inCycle(o)) return undefined;
    return byId.get(o.parentOwnerId);
  };

  const eff = new Map<string, number>();
  const compute = (o: OwnershipNode, guard: Set<string>): number => {
    const cached = eff.get(o.id);
    if (cached != null) return cached;
    // Belt-and-braces re-entrancy guard (parentOf already breaks true cycles).
    if (guard.has(o.id)) return 0;
    guard.add(o.id);
    const own = Number.isFinite(o.ownershipPct) ? o.ownershipPct : 0;
    const parent = parentOf(o);
    const value = parent ? (compute(parent, guard) * own) / 100 : own;
    eff.set(o.id, value);
    return value;
  };

  for (const o of owners) compute(o, new Set());
  return eff;
}
